import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import type { PublicClientApplication } from '@azure/msal-browser';
import { loadConfig } from '@/config';
import { initTelemetry } from '@/telemetry';
import { resolveAuthMode } from '@/auth/authMode';
import { createMsal } from '@/auth/msalInstance';
import { applyDebugAuthOverride } from '@/auth/debugAuthOverride';
import { App } from '@/app/App';
import { i18nReady } from '@/i18n';
import './index.css';

async function bootstrap(): Promise<void> {
  const baseConfig = await loadConfig();

  // Initialize telemetry before render so early errors are captured
  const telemetry = initTelemetry(baseConfig.appInsightsConnectionString);

  // Non-prod escape hatch (?debugClientId=...): lets testers point at a second
  // app registration (e.g. a temp app alongside the first-party app) without a
  // pipeline redeploy each time they need to switch. No-ops in production and
  // when no override is active. See debugAuthOverride.ts.
  const config = applyDebugAuthOverride(baseConfig);
  if (config.msalClientId !== baseConfig.msalClientId) {
    telemetry.trackEvent('aita.debug.auth_override_active', { msalClientId: config.msalClientId });
  }

  const authMode = resolveAuthMode(config);

  // Mock + real MSAL is a pointless combination — fail fast on misconfiguration.
  if (config.mockApi === true && authMode === 'msal') {
    throw new Error('Invalid config: mockApi cannot be combined with authMode "msal".');
  }

  // Dev-only MSW worker. import.meta.env.DEV is statically false in production
  // builds, so this branch (and the dynamic import) is dead-code-eliminated.
  if (import.meta.env.DEV && config.mockApi === true) {
    const { startMockWorker } = await import('@/mocks/browser');
    await startMockWorker();
  }

  // Real MSAL must be initialized (incl. handleRedirectPromise) BEFORE render.
  let pca: PublicClientApplication | undefined;
  let redirectPath: string | null = null;
  let redirectError: string | null = null;
  if (authMode === 'msal') {
    ({ pca, redirectPath, redirectError } = await createMsal(config));
    // A non-null redirectPath means this page load completed a login redirect
    // round-trip — the only reliable place to record auth success (loginRedirect's
    // promise never resolves, and the MSAL event fires before React mounts).
    if (redirectPath !== null) {
      telemetry.trackEvent('aita.auth.success');
    }
    // A failed sign-in (e.g. AADSTS500011 — account from a tenant without the
    // app's service principal) is user-recoverable: render signed out, let the
    // user retry from Landing with a different account. Never brick bootstrap.
    if (redirectError !== null) {
      telemetry.trackEvent('aita.auth.failure', { error: redirectError });
    }
  }

  // The locale load started at module import and ran in parallel with config/
  // MSAL setup; settle it before render so useSuspense:true components never
  // actually suspend (the chrome translates above the only Suspense boundary).
  await i18nReady;

  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found');
  }

  createRoot(rootElement).render(
    <StrictMode>
      <App config={config} pca={pca} redirectPath={redirectPath} redirectError={redirectError} />
    </StrictMode>,
  );
}

bootstrap().catch((error: unknown) => {
  const rootElement = document.getElementById('root');
  if (rootElement) {
    const container = document.createElement('div');
    container.style.cssText = 'padding: 2rem; font-family: system-ui;';

    const heading = document.createElement('h1');
    heading.textContent = 'Failed to start application';

    const message = document.createElement('p');
    message.textContent = error instanceof Error ? error.message : 'Unknown error';

    const hint = document.createElement('p');
    hint.textContent = 'Check that public/config/config.json exists and is valid.';

    container.append(heading, message, hint);
    rootElement.replaceChildren(container);
  }
});
